import { FileChild, HeadingLevel, Paragraph, TextRun } from 'docx';
import { getNavSlots } from '@/lib/elearningFrameNavSlots';
import { FrameDataType } from '@/modules/types/frame';
import {
  CONTENT_EMPTY,
  horizontalRuleParagraph,
  htmlContentHandler,
  htmlToInlineDocument,
  keyValuePairParagraph,
  scriptTagContentHandler,
  stripNonExportableElements,
  onclickContentHandler,
} from './common';
import { ElementType } from '@/modules/types/element';

/** Block-level / rich markup → use full `htmlContentHandler` (multiple paragraphs, lists, etc.). */
function shouldUseBlockHtmlExport(html: string): boolean {
  return /<\/?(p|div|ul|ol|table|h[1-6]|blockquote|pre|img|hr)\b/i.test(html);
}

function tooltipPrefixChildren(element: ElementType, index: number) {
  return [
    new TextRun({ text: `tip-${index + 1}: `, bold: true }),
    new TextRun({
      text:
        element.elementMetadata.type === 1
          ? 'Inline Tooltip'
          : 'Button Tooltip',
      italics: true,
    }),
    new TextRun({ text: '', break: 1 }),
    ...keyValuePairParagraph('Name: ', element.name),
    new TextRun({ text: '', break: 1 }),
    new TextRun({ text: 'Content:', bold: true }),
    new TextRun({ text: '', break: 1 }),
  ];
}

/** Renders frame tooltip rows: same labels as before; `content` as HTML via inline or block export. */
function handleFrameElementsToDoc(elements: ElementType[]): FileChild[] {
  return elements.flatMap((element, index) => {
    const prefix = tooltipPrefixChildren(element, index);
    const raw = element.content?.trim() ?? '';

    if (!raw) {
      return [
        new Paragraph({
          children: [
            ...prefix,
            new TextRun({ text: CONTENT_EMPTY }),
            new TextRun({ text: '', break: 1 }),
          ],
        }),
      ];
    }

    // block level
    if (shouldUseBlockHtmlExport(raw)) {
      return [
        new Paragraph({ children: prefix }),
        ...htmlContentHandler(raw),
        new Paragraph({
          children: [new TextRun({ text: '', break: 1 })],
        }),
      ];
    }

    // inline level
    const doc = new DOMParser().parseFromString(raw, 'text/html');
    stripNonExportableElements(doc);
    return [
      new Paragraph({
        children: [
          ...prefix,
          ...htmlToInlineDocument(doc.body),
          new TextRun({ text: '', break: 1 }),
        ],
      }),
    ];
  });
}

function frameToDocChildren(
  frame: FrameDataType,
  slideNumber: number,
): FileChild[] {
  return [
    new Paragraph({
      text: `Slide ${slideNumber}: ${frame.title?.trim()}`,
      heading: HeadingLevel.HEADING_3,
    }),
    new Paragraph({
      children: [new TextRun({ text: 'Description: ', bold: true })],
    }),
    new Paragraph({
      text: frame.description || CONTENT_EMPTY,
      children: [new TextRun({ text: '', break: 1 })],
    }),
    new Paragraph({
      children: [
        new TextRun({ text: 'Content: ', bold: true }),
        new TextRun({ text: '', break: 1 }),
      ],
    }),
    ...htmlContentHandler(frame.content || CONTENT_EMPTY),
    new Paragraph({
      text: '',
      children: [new TextRun({ text: '', break: 1 })],
    }),
    new Paragraph({
      text: 'Tooltips: ',
      heading: HeadingLevel.HEADING_4,
      children: [new TextRun({ text: '', break: 1 })],
    }),
    ...handleFrameElementsToDoc(frame.elements || []),

    // this is just a separator so we can tell where one frame ends and the next begins
    new Paragraph({
      text: '',
      children: [new TextRun({ text: '', break: 2 })],
    }),
    new Paragraph({
      text: 'Javascript content: ',
      heading: HeadingLevel.HEADING_4,
      children: [new TextRun({ text: '', break: 1 })],
    }),
    ...scriptTagContentHandler(frame.content || CONTENT_EMPTY),
    new Paragraph({
      text: '',
      children: [new TextRun({ text: '', break: 1 })],
    }),
    new Paragraph({
      text: 'Inline onclick events: ',
      heading: HeadingLevel.HEADING_4,
      children: [new TextRun({ text: '', break: 1 })],
    }),
    ...onclickContentHandler(frame.content || CONTENT_EMPTY),
    new Paragraph({
      text: '',
      children: [new TextRun({ text: '', break: 1 })],
    }),
  ];
}

export function handleFrameData(
  frames: FrameDataType[],
  units?: string | null,
): FileChild[] {
  const slots = getNavSlots(units ?? '', frames);
  const frameContent: FileChild[] = [
    new Paragraph({
      text: 'Frames',
      heading: HeadingLevel.HEADING_2,
    }),
  ];
  let slideNumber = 0;
  slots.forEach((slot) => {
    const sectionTitle = slot.unitTitle?.trim();
    if (sectionTitle) {
      frameContent.push(horizontalRuleParagraph());
      frameContent.push(
        new Paragraph({
          heading: HeadingLevel.HEADING_3,
          children: [
            new TextRun({ text: 'Section: ', bold: true }),
            new TextRun({ text: sectionTitle }),
            new TextRun({ text: '', break: 2 }),
          ],
        }),
      );
    }
    if (slot.frame) {
      frameContent.push(...frameToDocChildren(slot.frame, slideNumber));
      slideNumber += 1;
    }
  });
  return frameContent;
}
